import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Shield, AlertCircle, Scale, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TermsPage() {
  const navigate = useNavigate();

  const sections = [
    { id: 'acceptance', title: '1. Acceptance of Terms' },
    { id: 'services', title: '2. Description of Services' },
    { id: 'account', title: '3. Account Registration' },
    { id: 'transactions', title: '4. Transactions and Fees' },
    { id: 'prohibited', title: '5. Prohibited Activities' },
    { id: 'liability', title: '6. Limitation of Liability' },
    { id: 'termination', title: '7. Account Termination' },
    { id: 'changes', title: '8. Changes to Terms' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <FileText className="w-16 h-16 text-brand-blue mx-auto mb-6" />
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
            Terms of Service
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Please read these terms carefully before using Britedge services
          </p>
          <p className="text-sm text-slate-500 mt-4">Last Updated: December 2024</p>
        </div>
      </section>

      <section className="py-12 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-slate-900 mb-2">Important Notice</h3>
                <p className="text-slate-700 text-sm leading-relaxed">
                  By creating an account or using any Britedge service, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you must not use our services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-4 gap-12">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Table of Contents</h3>
            <nav className="space-y-2">
              {sections.map((section) => (
                <a 
                  key={section.id}
                  href={`#${section.id}`}
                  className="block text-sm text-slate-600 hover:text-brand-blue transition-colors py-1"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          <div className="lg:col-span-3 prose prose-lg max-w-none">
            <div id="acceptance" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">1. Acceptance of Terms</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                These Terms of Service ("Terms") constitute a legally binding agreement between you and Britedge Financial Services Limited ("Britedge", "we", "us", or "our") governing your access to and use of our platform, mobile applications, and related services (collectively, the "Services").
              </p>
              <p className="text-slate-600 leading-relaxed">
                By clicking "I Agree", creating an account, or accessing our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, our Privacy Policy, and any additional guidelines, rules, or disclaimers posted on our platform.
              </p>
            </div>

            <div id="services" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">2. Description of Services</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Britedge provides digital financial services including but not limited to:
              </p>
              <ul className="space-y-2 text-slate-700 mb-4">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-xs mt-1">✓</span>
                  <span>Electronic money transfers and bill payments</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-xs mt-1">✓</span>
                  <span>Savings accounts and goal-based savings products</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-xs mt-1">✓</span>
                  <span>Micro-loans and credit facilities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-xs mt-1">✓</span>
                  <span>Cooperative and group savings management</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-100 text-brand-blue flex items-center justify-center flex-shrink-0 text-xs mt-1">✓</span>
                  <span>Virtual and physical payment cards</span>
                </li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                We reserve the right to modify, suspend, or discontinue any part of the Services at any time with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuance of the Services.
              </p>
            </div>

            <div id="account" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">3. Account Registration and Security</h2>
              <div className="bg-slate-50 rounded-2xl p-6 mb-4">
                <h3 className="font-bold text-slate-900 mb-3">Eligibility Requirements</h3>
                <p className="text-slate-600 mb-2">To create a Britedge account, you must:</p>
                <ul className="space-y-1 text-slate-700 text-sm">
                  <li>• Be at least 18 years old</li>
                  <li>• Be a Nigerian citizen or legal resident</li>
                  <li>• Provide valid identification (BVN, NIN)</li>
                  <li>• Have a valid Nigerian phone number</li>
                  <li>• Complete our KYC verification process</li>
                </ul>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Account Security:</strong> You are responsible for maintaining the confidentiality of your account credentials. You agree to:
              </p>
              <ul className="space-y-2 text-slate-700">
                <li>• Use a strong, unique password</li>
                <li>• Enable two-factor authentication</li>
                <li>• Not share your PIN, password, or OTP with anyone</li>
                <li>• Notify us immediately of any unauthorized access</li>
                <li>• Log out from shared or public devices</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                You are fully responsible for all activities that occur under your account. Britedge will not be liable for any loss or damage arising from unauthorized access due to your failure to secure your account.
              </p>
            </div>

            <div id="transactions" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">4. Transactions and Fees</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Transaction Authorization:</strong> By initiating a transaction, you authorize Britedge to process the payment and deduct applicable fees from your account. All transactions are subject to our fraud prevention and security checks.
              </p>
              <div className="bg-white rounded-xl border border-slate-200 p-6 mb-4">
                <h3 className="font-bold text-slate-900 mb-3">Fee Structure</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Wallet-to-Wallet Transfer</span>
                    <span className="font-semibold">Free</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Wallet-to-Bank Transfer</span>
                    <span className="font-semibold">₦25</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Bill Payments</span>
                    <span className="font-semibold">Free</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Card Transactions (POS)</span>
                    <span className="font-semibold">0.75% - 1.5%</span>
                  </div>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">
                <strong>Transaction Limits:</strong> Account limits apply based on your verification tier. We reserve the right to decline, reverse, or hold any transaction that violates our policies or appears suspicious.
              </p>
            </div>

            <div id="prohibited" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">5. Prohibited Activities</h2>
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-4">
                <h3 className="font-bold text-slate-900 mb-3">You May NOT Use Our Services To:</h3>
                <ul className="space-y-2 text-slate-700 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>Engage in money laundering, terrorism financing, or fraud</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>Purchase illegal goods or services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>Conduct unauthorized pyramid schemes or Ponzi schemes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>Impersonate another person or entity</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>Violate any applicable laws or regulations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✗</span>
                    <span>Circumvent security measures or reverse engineer our systems</span>
                  </li>
                </ul>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Violation of these prohibitions may result in immediate account suspension or termination, and we may report such activities to relevant authorities.
              </p>
            </div>

            <div id="liability" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">6. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, BRITEDGE SHALL NOT BE LIABLE FOR:
              </p>
              <ul className="space-y-2 text-slate-700 mb-4">
                <li>• Indirect, incidental, special, or consequential damages</li>
                <li>• Loss of profits, data, or business opportunities</li>
                <li>• Service interruptions or technical failures</li>
                <li>• Unauthorized access resulting from your negligence</li>
                <li>• Third-party actions or bank processing delays</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                Our total liability to you for any claims shall not exceed the amount of fees you paid to us in the six months prior to the claim. This limitation applies regardless of the legal theory under which damages are claimed.
              </p>
            </div>

            <div id="termination" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">7. Account Termination</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Your Right to Terminate:</strong> You may close your account at any time by contacting customer support, provided:
              </p>
              <ul className="space-y-1 text-slate-700 mb-4">
                <li>• All outstanding balances are settled</li>
                <li>• No pending transactions exist</li>
                <li>• All loans are fully repaid</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Our Right to Terminate:</strong> We may suspend or terminate your account immediately if:
              </p>
              <ul className="space-y-1 text-slate-700 mb-4">
                <li>• You violate these Terms or applicable laws</li>
                <li>• We suspect fraudulent or suspicious activity</li>
                <li>• Required by law or regulatory authorities</li>
                <li>• Your account remains inactive for 24 months</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                Upon termination, any remaining balance will be returned to you minus applicable fees, unless prohibited by law.
              </p>
            </div>

            <div id="changes" className="mb-12">
              <h2 className="text-3xl font-bold font-heading text-slate-900 mb-6">8. Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting on our platform. We will notify you of material changes via email or in-app notification. Your continued use of the Services after changes are posted constitutes acceptance of the modified Terms. If you do not agree to the changes, you must stop using our Services and close your account.
              </p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 mt-12">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Contact Us</h3>
              <p className="text-slate-600 mb-4">
                For questions about these Terms of Service:
              </p>
              <div className="space-y-2 text-slate-700">
                <p><strong>Email:</strong> legal@britedge.ng</p>
                <p><strong>Phone:</strong> +234 (0) 800 BRITEDGE</p>
                <p><strong>Address:</strong> Plot 123, Admiralty Way, Lekki Phase 1, Lagos, Nigeria</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 px-4 md:px-8 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Scale className="w-12 h-12 mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-4">Governed by Nigerian Law</h3>
          <p className="text-slate-300">
            These Terms are governed by the laws of the Federal Republic of Nigeria. Any disputes shall be subject to the exclusive jurisdiction of Nigerian courts.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
